import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Dialog, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import * as Sentry from '@sentry/react';
import { TotpMultiFactorGenerator, multiFactor } from 'firebase/auth';
import { auth } from '../../../api/firebase/config';
import {
  generateMfaBackupCodes,
  getMfaBackupCodeStatus,
} from '../../../api/firebase/function/security/securityMfaFunction';
import {
  gray40,
  gray80,
  gray100,
  green50,
  orange10,
  orange40,
  primaryButtonBackgroundColor,
  red50,
  secondaryButtonBackgroundColor,
  tertiaryButtonBackgroundColor,
  white,
} from '../../../constants/colors';
import { MFA_BACKUP_CODE_COUNT, MFA_BACKUP_CODE_LOW_REMAINING } from '../../../constants/enum';
import {
  dialogTitle,
  infoRowActionButtonWidth,
  infoValue,
  regularMd,
  regularSm,
  regularXs,
} from '../../../constants/location';
import type { MfaBackupCodeStatus } from '../../../types/security';
import MfaEnrollDialog from './MfaEnrollDialog';
import MfaUnenrollDialog from './MfaUnenrollDialog';
import MfaBackupCodesView from './MfaBackupCodesView';

const styles = {
  statusText: {
    ...infoValue,
    alignItems: 'center',
    display: 'flex',
    lineHeight: '24px',
  },
  statusDot: {
    borderRadius: '50%',
    display: 'inline-block',
    height: '10px',
    marginRight: '8px',
    width: '10px',
  },
  actionButton: {
    '&:hover': {
      backgroundColor: white,
      border: `1px solid ${gray80}`,
      boxShadow: 'none',
      color: gray80,
    },
    backgroundColor: white,
    border: `1px solid ${gray80}`,
    boxShadow: 'none',
    color: gray80,
    fontSize: '14px',
    height: '32px',
    minWidth: infoRowActionButtonWidth,
    padding: '8px 16px',
    width: infoRowActionButtonWidth,
  },
  dialog: {
    '& .MuiPaper-root': {
      border: '1px solid' + gray40,
      boxSizing: 'border-box',
      margin: '16px',
      padding: '40px',
      width: '520px',
      '@media screen and (max-width:960px)': {
        padding: '32px 16px',
      },
    },
  },
  dialogHeader: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '24px',
  },
  dialogTitle,
  description: {
    ...regularSm,
    color: gray80,
    marginBottom: '16px',
  },
  dialogStatusRow: {
    alignItems: 'center',
    display: 'flex',
    ...regularMd,
    color: gray100,
    marginBottom: '16px',
  },
  backupCodeRow: {
    ...regularSm,
    color: gray100,
  },
  backupCodeAlert: {
    ...regularXs,
    backgroundColor: orange10,
    border: `1px solid ${orange40}`,
    borderRadius: '4px',
    color: orange40,
    marginTop: '8px',
    padding: '8px 12px',
  },
  caption: {
    ...regularXs,
    color: gray80,
    marginTop: '12px',
  },
  dialogButton: {
    height: '40px',
  },
  dialogActions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '12px',
    justifyContent: 'flex-end',
    marginTop: '24px',
  },
};

/**
 * アカウント管理「ログイン情報」内の二要素認証の行（#3132）。
 * 一覧には有効 / 未設定だけを出し、説明・バックアップコード残数・再発行・解除は
 * 「管理」ダイアログにまとめる。
 */
const MfaSettingRow: React.FC = () => {
  const [manageOpen, setManageOpen] = useState(false);
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [unenrollOpen, setUnenrollOpen] = useState(false);
  const [regenConfirmOpen, setRegenConfirmOpen] = useState(false);
  const [regenCodes, setRegenCodes] = useState<string[] | null>(null);
  const [regenSaved, setRegenSaved] = useState(false);
  const [regenLoading, setRegenLoading] = useState(false);
  const [backupStatus, setBackupStatus] = useState<MfaBackupCodeStatus | null>(null);
  // enrolledFactors は同期プロパティのため、変更後に再評価させるためのカウンタ
  const [refreshCount, setRefreshCount] = useState(0);

  const user = auth.currentUser;
  const enrolled =
    user !== null &&
    multiFactor(user).enrolledFactors.some(
      factor => factor.factorId === TotpMultiFactorGenerator.FACTOR_ID,
    );

  const refresh = useCallback((): void => {
    setRefreshCount(prev => prev + 1);
  }, []);

  // 画面表示時に ID トークンを更新して登録状況を取り直す（#3300）。
  // enrolledFactors はトークンのキャッシュを見るため、別セッションでの変更
  // （通知メールからの取り消し）が反映されず「有効」のまま残ることがある
  useEffect(() => {
    let cancelled = false;
    auth.currentUser
      ?.getIdToken(true)
      .then(() => {
        if (!cancelled) refresh();
      })
      .catch(() => {
        // 取得できない場合はキャッシュされた表示を維持する
      });
    return () => {
      cancelled = true;
    };
  }, [refresh]);

  // バックアップコードの残数を取得する（#3123）。取得できない場合は表示しない
  useEffect(() => {
    if (!enrolled) {
      setBackupStatus(null);
      return undefined;
    }
    let cancelled = false;
    getMfaBackupCodeStatus()
      .then(status => {
        if (!cancelled) setBackupStatus(status);
      })
      .catch(() => {
        if (!cancelled) setBackupStatus(null);
      });
    return () => {
      cancelled = true;
    };
  }, [enrolled, refreshCount]);

  const handleRegenerate = async (): Promise<void> => {
    setRegenLoading(true);
    try {
      const result = await generateMfaBackupCodes();
      setRegenSaved(false);
      setRegenCodes(result.codes);
      setRegenConfirmOpen(false);
      refresh();
    } catch (e) {
      Sentry.captureException(e);
    } finally {
      setRegenLoading(false);
    }
  };

  if (!user) return null;

  return (
    <>
      <Typography component="span" sx={styles.statusText}>
        <Box
          component="span"
          sx={[styles.statusDot, { backgroundColor: enrolled ? green50 : red50 }]}
        />
        {enrolled ? '有効' : '未設定'}
      </Typography>
      <Button sx={styles.actionButton} onClick={(): void => setManageOpen(true)}>
        {enrolled ? '管理' : '設定'}
      </Button>

      <Dialog open={manageOpen} onClose={(): void => setManageOpen(false)} sx={styles.dialog}>
        <Box sx={styles.dialogHeader}>
          <Typography sx={styles.dialogTitle}>二要素認証</Typography>
          <IconButton onClick={(): void => setManageOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        <Typography sx={styles.description}>
          ログイン時にパスワードに加えて、認証アプリ（Google Authenticator など）に表示される
          6桁のコードを入力する追加のセキュリティです。
        </Typography>
        <Typography component="div" sx={styles.dialogStatusRow}>
          <Box
            component="span"
            sx={[styles.statusDot, { backgroundColor: enrolled ? green50 : red50 }]}
          />
          {enrolled ? '有効' : '未設定'}
        </Typography>

        {enrolled && backupStatus !== null && (
          <>
            <Typography sx={styles.backupCodeRow}>
              バックアップコード: 残り {backupStatus.remaining} 個 /{' '}
              {backupStatus.total || MFA_BACKUP_CODE_COUNT} 個
            </Typography>
            {backupStatus.remaining === 0 && (
              <Typography sx={styles.backupCodeAlert}>
                バックアップコードを使い切りました。認証アプリを使用できないときにログインできなくなるため、
                「バックアップコードを再発行」から新しいコードを発行してください。
              </Typography>
            )}
            {backupStatus.remaining > 0 &&
              backupStatus.remaining <= MFA_BACKUP_CODE_LOW_REMAINING && (
                <Typography sx={styles.backupCodeAlert}>
                  バックアップコードの残りが少なくなっています。
                  「バックアップコードを再発行」から新しいコードを発行することをおすすめします。
                </Typography>
              )}
          </>
        )}
        {enrolled && (
          <Typography sx={styles.caption}>
            認証アプリを使用できない場合は、保存済みのバックアップコードでログインできます。
            コード1個につき1回ログインでき、認証アプリの設定はそのまま維持されます。
          </Typography>
        )}

        <Box sx={styles.dialogActions}>
          {enrolled ? (
            <>
              <Button
                onClick={(): void => setRegenConfirmOpen(true)}
                sx={[secondaryButtonBackgroundColor, styles.dialogButton]}
              >
                バックアップコードを再発行
              </Button>
              <Button
                onClick={(): void => setUnenrollOpen(true)}
                sx={[tertiaryButtonBackgroundColor, styles.dialogButton]}
              >
                解除する
              </Button>
            </>
          ) : (
            <Button
              onClick={(): void => setEnrollOpen(true)}
              sx={[primaryButtonBackgroundColor, styles.dialogButton]}
            >
              認証アプリを設定する
            </Button>
          )}
        </Box>
      </Dialog>

      <MfaEnrollDialog
        open={enrollOpen}
        onClose={(): void => setEnrollOpen(false)}
        onEnrolled={refresh}
      />
      <MfaUnenrollDialog
        open={unenrollOpen}
        onClose={(): void => setUnenrollOpen(false)}
        onUnenrolled={refresh}
      />

      {/* バックアップコード再発行の確認 */}
      <Dialog
        open={regenConfirmOpen}
        onClose={(): void => {
          if (!regenLoading) setRegenConfirmOpen(false);
        }}
        sx={styles.dialog}
      >
        <Box sx={styles.dialogHeader}>
          <Typography sx={styles.dialogTitle}>バックアップコードを再発行しますか？</Typography>
          <IconButton
            onClick={(): void => setRegenConfirmOpen(false)}
            disabled={regenLoading}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Typography sx={styles.description}>
          再発行すると、以前のバックアップコードはすべて使用できなくなります。
        </Typography>
        <Box sx={styles.dialogActions}>
          <Button
            onClick={(): void => setRegenConfirmOpen(false)}
            disabled={regenLoading}
            sx={[tertiaryButtonBackgroundColor, styles.dialogButton]}
          >
            キャンセル
          </Button>
          <Button
            onClick={handleRegenerate}
            disabled={regenLoading}
            startIcon={regenLoading ? <CircularProgress size={16} color="inherit" /> : undefined}
            sx={[primaryButtonBackgroundColor, styles.dialogButton]}
          >
            再発行する
          </Button>
        </Box>
      </Dialog>

      {/* 再発行したコードの表示（保存するまで閉じられない） */}
      <Dialog open={regenCodes !== null} sx={styles.dialog}>
        <Box sx={styles.dialogHeader}>
          <Typography sx={styles.dialogTitle}>新しいバックアップコード</Typography>
        </Box>
        <MfaBackupCodesView
          codes={regenCodes ?? []}
          email={user.email ?? ''}
          onSavedChange={setRegenSaved}
        />
        <Box sx={styles.dialogActions}>
          <Button
            onClick={(): void => setRegenCodes(null)}
            disabled={!regenSaved}
            sx={[primaryButtonBackgroundColor, styles.dialogButton]}
          >
            完了
          </Button>
        </Box>
      </Dialog>
    </>
  );
};

export default MfaSettingRow;
